import type { AuthError, Session, User } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";

/**
 * Session state, backed by Supabase Auth.
 *
 * `onAuthStateChange` fires once on mount with the restored session (or null), which is
 * what ends the initial loading state, and again on every sign-in, sign-out and silent
 * token refresh. Nothing here stores a token itself -- supabase-js owns that, and
 * `lib/api.ts` asks it for a fresh one per request.
 */

interface AuthState {
    user: User | null;
    /** Preferred name, from the signup metadata; falls back to the email local part. */
    displayName: string;
    /** True until the initial session restore has resolved. */
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    /** Resolves with `checkInbox` when the flow ends at the inbox rather than at a session. */
    signUp: (email: string, password: string, displayName: string) => Promise<{ checkInbox: boolean }>;
    /** Mail a password-reset link. Resolves the same way for unknown addresses -- see below. */
    requestPasswordReset: (email: string) => Promise<void>;
    /** Set a new password for the session the recovery link opened. */
    updatePassword: (password: string) => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function nameOf(user: User | null): string {
    if (!user) return "";
    const metadata = user.user_metadata as { display_name?: unknown } | undefined;
    if (typeof metadata?.display_name === "string" && metadata.display_name.trim()) {
        return metadata.display_name.trim();
    }
    return user.email?.split("@")[0] ?? "";
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession);
            setLoading(false);
        });
        return () => data.subscription.unsubscribe();
    }, []);

    const signIn = useCallback(async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(translate(error.message));
    }, []);

    const signUp = useCallback(async (email: string, password: string, displayName: string) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            // Read back by `nameOf` above, and copied into user_settings.display_name by
            // the on_auth_user_created trigger.
            options: { data: { display_name: displayName } }
        });
        // An address that is already taken must be indistinguishable from a fresh one, so
        // this branch reports nothing back: both end on the same "check your inbox" screen,
        // and only the contents of that inbox differ. The owner of the address learns that
        // someone tried to register it from the recovery mail below; a stranger probing for
        // registered addresses learns nothing.
        if (isAddressTaken(data.user, error)) {
            await notifyExistingAccount(email);
            return { checkInbox: true };
        }

        if (error) throw new Error(translate(error.message));

        // With email confirmation enabled, signUp returns a user but no session; the
        // learner has to click the link before they can sign in.
        return { checkInbox: data.session === null };
    }, []);

    const requestPasswordReset = useCallback(async (email: string) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            // Must be covered by Authentication -> URL Configuration -> Redirect URLs, or the
            // link in the mail falls back to the Site URL and lands on the dashboard instead.
            redirectTo: `${window.location.origin}/reset-password`
        });
        // Note this resolves identically for an address that was never registered -- GoTrue
        // will not say. The UI has to be worded so it does not claim the mail was sent.
        if (error) throw new Error(translate(error.message));
    }, []);

    const updatePassword = useCallback(async (password: string) => {
        // Works against whatever session is current, which on /reset-password is the one the
        // recovery link just opened.
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw new Error(translate(error.message));
    }, []);

    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, []);

    const value = useMemo<AuthState>(
        () => ({
            user: session?.user ?? null,
            displayName: nameOf(session?.user ?? null),
            loading,
            signIn,
            signUp,
            requestPasswordReset,
            updatePassword,
            signOut
        }),
        [session, loading, signIn, signUp, requestPasswordReset, updatePassword, signOut]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Did `signUp` just bounce off an address that already has an account?
 *
 * Two answer shapes, and both have to be caught, because which one arrives depends on where
 * GoTrue is running rather than on anything this app controls:
 *
 * - A `user_already_exists` error (HTTP 422). What the CLI's local stack returns, verified
 *   against it -- and note it returns this with `enable_confirmations` on, so confirmation
 *   settings are not the switch here.
 * - No error at all, and a fabricated user whose `identities` is an empty array. This is
 *   Supabase Cloud's email enumeration protection mimicking a fresh signup awaiting
 *   confirmation; the empty array is the only tell. (Undefined is not empty -- a real
 *   signup carries `identities: [{provider: "email"}]`, so only an actual `[]` counts.)
 *
 * Deliberately *not* covered: an address registered but never confirmed. GoTrue returns the
 * real user there, with identities intact, and resends the confirmation mail -- which is the
 * right outcome already, and reaches the same inbox screen without needing this branch.
 */
function isAddressTaken(user: User | null, error: AuthError | null): boolean {
    if (error) return error.code === "user_already_exists" || error.message === "User already registered";
    return user?.identities?.length === 0;
}

/**
 * Let the owner of an already-registered address know that someone tried to sign up with
 * it, by sending them the recovery mail -- whose template is worded to cover both arrivals
 * (see `supabase/templates/recovery.html`). Supabase's mailer sends only its own fixed set
 * of mails, so this is the one that can carry the notice without standing up an SMTP
 * provider of our own.
 *
 * Failures are swallowed deliberately. GoTrue refuses a second send inside `max_frequency`,
 * and surfacing that refusal to the signup form would re-open exactly the disclosure this
 * whole branch exists to close.
 */
async function notifyExistingAccount(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) console.warn("could not mail the signup notice for an existing address:", error.message);
}

/** Supabase returns English error strings; surface the common ones in Chinese. */
function translate(message: string): string {
    const table: Record<string, string> = {
        "Invalid login credentials": "邮箱或密码不正确",
        "Email not confirmed": "邮箱还没有验证，请先点开验证邮件里的链接",
        // No entry for "User already registered" on purpose: `isAddressTaken` intercepts it
        // before it can reach a form, and translating it would only make it easier to put
        // back on screen.
        "Password should be at least 6 characters": "密码太短",
        "Signups not allowed for this instance": "此实例已关闭注册",
        "Auth session missing!": "重置链接已失效，请重新申请一封邮件",
        "New password should be different from the old password.": "新密码不能和旧密码相同",
        "Email rate limit exceeded": "邮件发送太频繁了，请过一会儿再试"
    };
    if (table[message]) return table[message];

    // "For security purposes, you can only request this after 47 seconds." -- the number
    // varies, so it cannot be a table key.
    if (message.startsWith("For security purposes")) return "操作太频繁了，请过一会儿再试";
    return message;
}

export function useAuth(): AuthState {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
    return context;
}
